import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import EditFilledIcon from '@atlaskit/icon/glyph/edit-filled';
import FormTextField from './FormTextField';
import { colors, gridSize } from '@atlaskit/theme';

import FieldGroup from './FieldGroup';
import FieldIconContainer from './FieldIconContainer';
import { media } from '../utilities/style-utils';
import messages from './EmailContainer.messages';

import { useIntl } from 'react-intl';
import { usePrevious } from '../utilities/hooks';

// Offset FieldGroup negative margin by adding a top margin here.
const ReadOnlyEmail = styled.div`
  margin-top: ${gridSize()}px;
  border: 1px solid rgba(255, 255, 255, 0);
  border-radius: 5px;
  box-sizing: border-box;
  cursor: pointer;
  height: 39px;
  outline: none;
  padding: 9px 32px 9px 8px;
  transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out;

  &:hover {
    background: ${colors.N10};
    border-color: ${colors.N20};
  }
  &:focus {
    border-color: ${colors.B100};
    border-width: 2px;
    padding: 8px 31px 8px 7px;
  }

  ${media.handheld`
    font-size: 16px;
  `}
`;
const ReadOnlyEmailText = styled.span`
  display: flex;
  overflow: hidden;
  white-space: nowrap;
`;
const StyledFieldIconContainer = styled(FieldIconContainer)`
  float: right;
  margin-top: -22px;
  margin-right: -27px;
`;

interface EmailProps {
  isReadOnly?: boolean;
  fieldError?: string;
  onChange: (value: string) => void;
  onEmailClick: () => void;
  value: string;
  placeholderOverride?: string;
  inputRef: React.RefObject<HTMLInputElement>;
}

const EmailContainer = ({
  fieldError,
  isReadOnly,
  onChange,
  onEmailClick,
  placeholderOverride,
  value,
  inputRef,
}: EmailProps) => {
  const { formatMessage } = useIntl();

  const prevIsReadOnly = usePrevious(isReadOnly);

  useEffect(() => {
    if (!isReadOnly && prevIsReadOnly) {
      inputRef.current?.focus();
    }
  }, [isReadOnly, prevIsReadOnly, inputRef]);

  const handleChange = (e: React.FormEvent<HTMLInputElement>) => {
    const newValue = e.currentTarget.value;
    onChange(newValue);
  };

  const handleReadOnlyKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onEmailClick();
    }
  };

  const placeholder = placeholderOverride || formatMessage(messages.loginFormUsernamePlaceholder);
  return (
    <FieldGroup>
      {isReadOnly ? (
        <ReadOnlyEmail onClick={onEmailClick} tabIndex={0} onKeyDown={handleReadOnlyKeyDown}>
          <ReadOnlyEmailText>{value}</ReadOnlyEmailText>
          <StyledFieldIconContainer>
            <EditFilledIcon label="" />
          </StyledFieldIconContainer>
        </ReadOnlyEmail>
      ) : (
        <FormTextField
          id="username"
          testId="username" // used in pollinators
          name="username"
          autoComplete="username"
          type="email"
          placeholder={placeholder}
          label={formatMessage(messages.loginFormUsernameLabel)}
          autoFocus={!isReadOnly}
          isLabelHidden={true}
          onChange={handleChange}
          isInvalid={!!fieldError}
          invalidMessage={fieldError}
          value={value}
          ref={inputRef}
        />
      )}
    </FieldGroup>
  );
};

export default EmailContainer;
